/**
 * Trace view: every gen-api call one scan (or one upstream job) produced, read
 * as the sequence it actually happened in and split per view.
 *
 * This is a debugging surface, not an analytics one — the question it answers is
 * "which model was called with which prompt, in what order, and what came back",
 * so the rows stay dense and the prompt is always on screen.
 *
 * Only calls that reached gen-api over HTTP appear here, since those are what
 * carry the scan/job/view envelope. Work a pipeline did by calling a provider
 * directly is logged on that pipeline's side instead.
 */
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchModels, fetchTraceCalls, fetchTraces } from '../api';
import { CopyActionButton, InlineCopyButton } from '../components/apiSnippets';
import { EndpointBadge } from '../components/EndpointBadge';
import { ImageLightbox } from '../components/ImageLightbox';
import { recomputeCost, usePricingVersion } from '../pricingOverrides';
import { endpointLabel, formatSeconds, formatTimestamp } from '../stress/format';
import type { ApiCallRecord, ApiEndpointModel, ApiTraceRow } from '../types';

/** How an entered id is looked up. `auto` probes scan first, then job. */
type SearchMode = 'auto' | 'scan' | 'job';

/** Window covered by the browse list. */
const BROWSE_RANGE_MS = 7 * 24 * 60 * 60_000;
const IN_FLIGHT_POLL_MS = 3000;

/**
 * Vehicle-walk order for the per-view sections, so the sequence reads the way
 * the cameras are laid out rather than alphabetically. Matched as a substring,
 * because callers stamp anything from `left` to `left_front` to `at_cam_03`.
 */
const VIEW_ORDER = ['front', 'left', 'rear', 'right', 'top', 'undercarriage', 'interior'];

function isInProgress(status: string): boolean {
  return status === 'pending' || status === 'processing';
}

function viewRank(view: string): number {
  const lower = view.toLowerCase();
  const idx = VIEW_ORDER.findIndex((v) => lower.includes(v));
  return idx < 0 ? VIEW_ORDER.length : idx;
}

function formatUsd(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value <= 0) return '$0';
  return value < 0.01 ? `$${value.toFixed(5)}` : `$${value.toFixed(4)}`;
}

function formatClock(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString();
}

/** Gap between a call and the one before it in the same view. */
function formatGap(iso: string | null, previousIso: string | null): string | null {
  if (!iso || !previousIso) return null;
  const ms = new Date(iso).getTime() - new Date(previousIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return `+${formatSeconds(ms / 1000)}`;
}

interface ViewGroup {
  view: string;
  calls: ApiCallRecord[];
}

/** Split a trace per view, ordered along the vehicle then alphabetically. */
function groupByView(calls: ApiCallRecord[]): ViewGroup[] {
  const map = new Map<string, ApiCallRecord[]>();
  for (const call of calls) {
    const key = call.view || '';
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(call);
    } else {
      map.set(key, [call]);
    }
  }
  return Array.from(map.entries())
    .map(([view, rows]) => ({ view, calls: rows }))
    .sort((a, b) => {
      // Calls that carried no view can't be attributed, so they sit last.
      if (!a.view) return 1;
      if (!b.view) return -1;
      const rank = viewRank(a.view) - viewRank(b.view);
      return rank !== 0 ? rank : a.view.localeCompare(b.view);
    });
}

export function TracePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scanParam = searchParams.get('scan_id') || '';
  const jobParam = searchParams.get('job_id') || '';
  const queryParam = searchParams.get('q') || '';
  const target = scanParam || jobParam || queryParam;

  const [input, setInput] = useState(target);
  const [mode, setMode] = useState<SearchMode>(
    scanParam ? 'scan' : jobParam ? 'job' : 'auto',
  );

  const [calls, setCalls] = useState<ApiCallRecord[] | null>(null);
  // Which field actually matched, so the header can say what it is looking at
  // even when `auto` resolved it.
  const [resolvedBy, setResolvedBy] = useState<'scan' | 'job'>('scan');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [traces, setTraces] = useState<ApiTraceRow[] | null>(null);
  const [tracesError, setTracesError] = useState<string | null>(null);

  const [models, setModels] = useState<ApiEndpointModel[]>([]);
  const pricingVersion = usePricingVersion();

  // Follow the URL rather than the box: a crosslink or a browse-list click
  // changes the params, and the box should show what is being displayed.
  useEffect(() => {
    setInput(scanParam || jobParam || queryParam);
    if (scanParam) setMode('scan');
    else if (jobParam) setMode('job');
    else if (queryParam) setMode('auto');
  }, [scanParam, jobParam, queryParam]);

  useEffect(() => {
    let cancelled = false;
    fetchModels()
      .then((m) => {
        if (!cancelled) setModels(m);
      })
      .catch(() => {
        // Labels and pricing overrides are best-effort; raw model_type will do.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Browse list: which scans/jobs recently sent work to gen-api.
  useEffect(() => {
    let cancelled = false;
    const to = Date.now();
    fetchTraces({ from: to - BROWSE_RANGE_MS, to, limit: 100 })
      .then((rows) => {
        if (!cancelled) {
          setTraces(rows);
          setTracesError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setTracesError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    if (!target) {
      setCalls(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        let rows: ApiCallRecord[];
        let by: 'scan' | 'job';
        if (scanParam) {
          rows = await fetchTraceCalls({ scanId: scanParam });
          by = 'scan';
        } else if (jobParam) {
          rows = await fetchTraceCalls({ jobId: jobParam });
          by = 'job';
        } else {
          // Auto: most ids pasted here are scan ids, so try that first and only
          // fall back to the job column when it turns up nothing.
          rows = await fetchTraceCalls({ scanId: queryParam });
          by = 'scan';
          if (rows.length === 0) {
            rows = await fetchTraceCalls({ jobId: queryParam });
            by = 'job';
          }
        }
        if (!cancelled) {
          setCalls(rows);
          setResolvedBy(by);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setCalls(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [target, scanParam, jobParam, queryParam, reloadKey]);

  // A trace opened while the scan is still running should finish filling in.
  const hasInFlight = useMemo(
    () => (calls || []).some((c) => isInProgress(c.status)),
    [calls],
  );
  useEffect(() => {
    if (!hasInFlight || !target) return;
    const id = window.setInterval(() => setReloadKey((k) => k + 1), IN_FLIGHT_POLL_MS);
    return () => window.clearInterval(id);
  }, [hasInFlight, target]);

  const modelByKey = useMemo(
    () => new Map(models.map((m) => [m.key, m])),
    [models],
  );

  /** Recorded cost re-priced under any local override for that endpoint. */
  const costOf = useMemo(() => {
    void pricingVersion; // recompute when the pricing override changes
    return (record: ApiCallRecord): number | null => {
      const model = modelByKey.get(record.model_type);
      if (!model) return record.cost_usd;
      return recomputeCost(model, record.cost_usd).cost;
    };
  }, [modelByKey, pricingVersion]);

  const groups = useMemo(() => groupByView(calls || []), [calls]);

  const summary = useMemo(() => {
    const rows = calls || [];
    let cost = 0;
    let errors = 0;
    let firstAt: number | null = null;
    let lastAt: number | null = null;
    for (const row of rows) {
      cost += costOf(row) ?? 0;
      if (row.status === 'error') errors += 1;
      const start = row.created_at ? new Date(row.created_at).getTime() : NaN;
      const end = new Date(row.finished_at || row.created_at || '').getTime();
      if (Number.isFinite(start) && (firstAt === null || start < firstAt)) firstAt = start;
      if (Number.isFinite(end) && (lastAt === null || end > lastAt)) lastAt = end;
    }
    return {
      calls: rows.length,
      errors,
      cost,
      // Counts named views only, so it agrees with the browse list — the
      // no-view bucket is a section here but isn't a view.
      views: new Set(rows.filter((r) => r.view).map((r) => r.view)).size,
      endpoints: new Set(rows.map((r) => r.model_type)).size,
      wall: firstAt !== null && lastAt !== null ? (lastAt - firstAt) / 1000 : null,
      player: rows.find((r) => r.player)?.player || null,
      organizationId: rows.find((r) => r.organization_id)?.organization_id || null,
      scanId: rows.find((r) => r.scan_id)?.scan_id || (resolvedBy === 'scan' ? target : ''),
      jobId: rows.find((r) => r.job_id)?.job_id || (resolvedBy === 'job' ? target : ''),
    };
  }, [calls, costOf, resolvedBy, target]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const value = input.trim();
    const next = new URLSearchParams();
    if (value) {
      if (mode === 'scan') next.set('scan_id', value);
      else if (mode === 'job') next.set('job_id', value);
      else next.set('q', value);
    }
    setSearchParams(next);
  }

  function openTrace(row: ApiTraceRow) {
    const next = new URLSearchParams();
    if (row.scan_id) next.set('scan_id', row.scan_id);
    else next.set('job_id', row.job_id);
    setSearchParams(next);
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Trace</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every gen-api call a scan or job made, in order, grouped by view — which model
            ran with which prompt, and what came back.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-indigo-300"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h5M20 20v-5h-5M5 19a9 9 0 0014-6M19 5a9 9 0 00-14 6" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Search */}
      <form
        onSubmit={submit}
        className="bg-white rounded-xl border border-slate-200 shadow-soft p-4 flex flex-col md:flex-row md:items-end gap-3"
      >
        <label className="block flex-1">
          <span className="text-xs font-medium text-slate-500">Scan ID or Job ID</span>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="fe264bea-a763-41cc-98c2-06246bfa093f"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
          />
        </label>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Look up as</span>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {(
              [
                ['auto', 'Auto'],
                ['scan', 'Scan'],
                ['job', 'Job'],
              ] as [SearchMode, string][]
            ).map(([value, label], idx) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                title={
                  value === 'auto'
                    ? 'Try the scan id first, then the job id'
                    : `Match the ${value} id only`
                }
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  idx > 0 ? 'border-l border-slate-200' : ''
                } ${
                  mode === value
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-semibold hover:opacity-95"
        >
          Trace
        </button>
        {target && (
          <button
            type="button"
            onClick={() => setSearchParams(new URLSearchParams())}
            className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Clear
          </button>
        )}
      </form>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          Failed to load trace: {error}
        </div>
      )}

      {target ? (
        <>
          <TraceSummary
            summary={summary}
            resolvedBy={resolvedBy}
            target={target}
            loading={loading}
          />

          {loading && calls === null && (
            <div className="text-sm text-slate-500">Loading trace…</div>
          )}

          {!loading && calls !== null && calls.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white/50 px-4 py-12 text-center text-sm text-slate-400">
              No gen-api calls recorded for{' '}
              <span className="font-mono text-slate-500">{target}</span>. Only calls that
              reached gen-api over HTTP carry a scan/job id — work a pipeline did by calling
              a provider directly is logged on that pipeline's side.
            </div>
          )}

          {groups.map((group) => (
            <ViewSection
              key={group.view || '__none__'}
              group={group}
              modelByKey={modelByKey}
              costOf={costOf}
            />
          ))}
        </>
      ) : (
        <RecentTraces
          traces={traces}
          error={tracesError}
          onOpen={openTrace}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trace header
// ---------------------------------------------------------------------------

interface Summary {
  calls: number;
  errors: number;
  cost: number;
  views: number;
  endpoints: number;
  wall: number | null;
  player: string | null;
  organizationId: string | null;
  scanId: string;
  jobId: string;
}

function TraceSummary({
  summary,
  resolvedBy,
  target,
  loading,
}: {
  summary: Summary;
  resolvedBy: 'scan' | 'job';
  target: string;
  loading: boolean;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {resolvedBy === 'scan' ? 'Scan' : 'Job'}
        </span>
        <code className="text-sm font-mono text-slate-800 break-all">{target}</code>
        <InlineCopyButton value={target} title="Copy id" />
        {loading && <span className="text-xs text-slate-400">refreshing…</span>}
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Calls" value={String(summary.calls)} />
        <Stat
          label="Errors"
          value={String(summary.errors)}
          tone={summary.errors > 0 ? 'danger' : undefined}
        />
        <Stat label="Views" value={String(summary.views)} />
        <Stat label="Endpoints" value={String(summary.endpoints)} />
        <Stat label="Cost" value={formatUsd(summary.cost)} />
        <Stat label="Wall time" value={formatSeconds(summary.wall)} />
      </div>

      {(summary.player || summary.organizationId || summary.scanId || summary.jobId) && (
        <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 border-t border-slate-100 pt-3">
          {summary.scanId && <MetaPair label="Scan" value={summary.scanId} mono />}
          {summary.jobId && <MetaPair label="Job" value={summary.jobId} mono />}
          {summary.organizationId && (
            <MetaPair label="Organization" value={summary.organizationId} mono />
          )}
          {summary.player && <MetaPair label="Player" value={summary.player} />}
        </dl>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'danger';
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div
        className={`text-lg font-semibold ${tone === 'danger' ? 'text-danger' : 'text-slate-900'}`}
      >
        {value}
      </div>
    </div>
  );
}

function MetaPair({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <dt className="text-slate-400 shrink-0">{label}</dt>
      <dd className={`text-slate-600 truncate ${mono ? 'font-mono' : ''}`} title={value}>
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One view's call sequence
// ---------------------------------------------------------------------------

function ViewSection({
  group,
  modelByKey,
  costOf,
}: {
  group: ViewGroup;
  modelByKey: Map<string, ApiEndpointModel>;
  costOf: (record: ApiCallRecord) => number | null;
}) {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [lightbox, setLightbox] = useState<string | null>(null);

  function toggleRow(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const cost = group.calls.reduce((sum, c) => sum + (costOf(c) ?? 0), 0);
  const errors = group.calls.filter((c) => c.status === 'error').length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-2 min-w-0">
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h2 className="text-sm font-semibold text-slate-900 truncate">
            {group.view || <span className="text-slate-400 italic">No view</span>}
          </h2>
          <span className="text-xs text-slate-400">
            {group.calls.length} call{group.calls.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs shrink-0">
          {errors > 0 && (
            <span className="font-semibold text-danger">
              {errors} error{errors === 1 ? '' : 's'}
            </span>
          )}
          <span className="text-slate-500 font-mono">{formatUsd(cost)}</span>
        </div>
      </button>

      {open && (
        <div className="overflow-x-auto border-t border-slate-100">
          <table className="data-table data-table-scroll w-full">
            <thead>
              <tr>
                <th className="px-3 py-2 w-8">#</th>
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Endpoint</th>
                <th className="px-3 py-2">Served by</th>
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2">Prompt</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Dur</th>
                <th className="px-3 py-2 text-right">Cost</th>
                <th className="px-3 py-2 text-right">Retries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {group.calls.map((call, idx) => (
                <CallRows
                  key={call.id}
                  call={call}
                  index={idx + 1}
                  gap={formatGap(call.created_at, idx > 0 ? group.calls[idx - 1].created_at : null)}
                  model={modelByKey.get(call.model_type)}
                  cost={costOf(call)}
                  expanded={expanded.has(call.id)}
                  onToggle={() => toggleRow(call.id)}
                  onExpandImage={setLightbox}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lightbox && <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function CallRows({
  call,
  index,
  gap,
  model,
  cost,
  expanded,
  onToggle,
  onExpandImage,
}: {
  call: ApiCallRecord;
  index: number;
  gap: string | null;
  model: ApiEndpointModel | undefined;
  cost: number | null;
  expanded: boolean;
  onToggle: () => void;
  onExpandImage: (url: string) => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={`cursor-pointer align-top ${expanded ? 'bg-indigo-50/40' : ''}`}
        title="Click for the full prompt, params and images"
      >
        <td className="px-3 py-1.5 text-slate-400 font-mono">{index}</td>
        <td className="px-3 py-1.5 whitespace-nowrap">
          <span className="font-mono text-slate-600">{formatClock(call.created_at)}</span>
          {gap && <span className="ml-1.5 text-slate-400">{gap}</span>}
        </td>
        <td className="px-3 py-1.5 whitespace-nowrap text-slate-700">
          {model?.label || endpointLabel(call.model_type)}
        </td>
        <td className="px-3 py-1.5">
          <EndpointBadge provider={call.provider} modelId={call.model_id} />
        </td>
        <td className="px-3 py-1.5 whitespace-nowrap text-slate-500 font-mono">
          {call.generation_tier || '—'}
        </td>
        <td className="px-3 py-1.5">
          <div
            className="max-w-[380px] truncate font-mono text-slate-600"
            title={call.prompt || undefined}
          >
            {call.prompt || <span className="text-slate-300 italic">no prompt</span>}
          </div>
        </td>
        <td className="px-3 py-1.5 whitespace-nowrap">
          <StatusBadge status={call.status} />
          {call.dummy && (
            <span
              className="ml-1 inline-flex items-center rounded bg-slate-200 px-1 py-0.5 text-[10px] font-semibold text-slate-600"
              title="Dummy call: no provider was reached"
            >
              dummy
            </span>
          )}
          {call.retry_system_instruction && (
            <span
              className="ml-1 inline-flex items-center rounded bg-orange-100 px-1 py-0.5 text-[10px] font-semibold text-orange-700"
              title="The provider's policy filter rejected this input; a system instruction was attached to salvage it"
            >
              safety
            </span>
          )}
        </td>
        <td className="px-3 py-1.5 text-right whitespace-nowrap font-mono text-slate-600">
          {formatSeconds(call.duration_seconds)}
        </td>
        <td className="px-3 py-1.5 text-right whitespace-nowrap font-mono text-slate-600">
          {formatUsd(cost)}
        </td>
        <td className="px-3 py-1.5 text-right whitespace-nowrap font-mono">
          {call.retries != null && call.retries > 0 ? (
            <span className="text-amber-600 font-semibold">{call.retries}</span>
          ) : (
            <span className="text-slate-300">—</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/70">
          <td colSpan={10} className="px-3 py-3">
            <CallDetail call={call} model={model} onExpandImage={onExpandImage} />
          </td>
        </tr>
      )}
    </>
  );
}

function CallDetail({
  call,
  model,
  onExpandImage,
}: {
  call: ApiCallRecord;
  model: ApiEndpointModel | undefined;
  onExpandImage: (url: string) => void;
}) {
  const params = call.params || {};
  const paramEntries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  const inputEntries = Object.entries(call.input_refs || {});
  const slug = model ? model.path.replace(/^\/v1\//, '') : null;

  return (
    <div className="space-y-3">
      {call.status === 'error' && call.error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
          <DetailTitle>Error</DetailTitle>
          <pre className="text-xs text-danger whitespace-pre-wrap break-words">{call.error}</pre>
        </div>
      )}

      <div>
        <DetailTitle>
          {call.retry_system_instruction ? 'Prompt (sent unchanged)' : 'Prompt'}
        </DetailTitle>
        {call.prompt ? (
          <pre className="text-xs text-slate-700 whitespace-pre-wrap break-words bg-white rounded-lg border border-slate-200 p-3">
            {call.prompt}
          </pre>
        ) : (
          <p className="text-xs text-slate-400 italic">
            This endpoint takes no prompt.
          </p>
        )}
      </div>

      {call.retry_system_instruction && (
        <div>
          <DetailTitle>Safety retry — added system instruction</DetailTitle>
          <pre className="text-xs text-slate-700 whitespace-pre-wrap break-words bg-orange-50 rounded-lg border border-orange-200 p-3">
            {call.retry_system_instruction}
          </pre>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div>
          <DetailTitle>Call</DetailTitle>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
            <DetailField label="Request ID" value={call.id} mono copy />
            <DetailField label="Created" value={formatTimestamp(call.created_at)} />
            <DetailField label="Model type" value={call.model_type} mono />
            <DetailField label="Model ID" value={call.model_id} mono />
            <DetailField label="Provider" value={call.provider} />
            <DetailField label="Tier" value={call.generation_tier || null} mono />
            <DetailField label="View" value={call.view} />
            <DetailField label="DVP" value={call.dvp} />
            {call.seed != null && <DetailField label="Seed" value={String(call.seed)} mono />}
            {paramEntries.map(([k, v]) => (
              <DetailField key={k} label={k} value={String(v)} mono />
            ))}
          </dl>
        </div>

        <div>
          <DetailTitle>Images</DetailTitle>
          <div className="flex flex-wrap gap-2">
            {inputEntries.map(([name, url]) => (
              <Thumb key={name} label={name} url={url} onExpand={onExpandImage} />
            ))}
            <Thumb label="output" url={call.output_ref} highlight onExpand={onExpandImage} />
            {inputEntries.length === 0 && !call.output_ref && (
              <p className="text-xs text-slate-400">No stored images for this call.</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {call.prompt && (
          <CopyActionButton value={call.prompt} icon="copy">
            Copy prompt
          </CopyActionButton>
        )}
        <CopyActionButton value={JSON.stringify(call, null, 2)} icon="copy">
          Copy record JSON
        </CopyActionButton>
        {slug && (
          <Link
            to={`/gen-api/${slug}?tab=requests`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-indigo-300"
          >
            Open in {model?.label || call.model_type}
          </Link>
        )}
        {call.output_ref && (
          <a
            href={call.output_ref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-indigo-300"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open output
          </a>
        )}
      </div>
    </div>
  );
}

function DetailTitle({ children }: { children: ReactNode }) {
  return (
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
      {children}
    </h4>
  );
}

function DetailField({
  label,
  value,
  mono,
  copy,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  copy?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-100 py-0.5">
      <dt className="text-[11px] text-slate-400 shrink-0">{label}</dt>
      <dd className={`text-[11px] text-slate-700 text-right break-all ${mono ? 'font-mono' : ''}`}>
        {value ? (
          <span className="inline-flex items-center gap-1.5">
            {value}
            {copy && <InlineCopyButton value={value} title={`Copy ${label}`} />}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </dd>
    </div>
  );
}

function Thumb({
  label,
  url,
  highlight,
  onExpand,
}: {
  label: string;
  url: string | null;
  highlight?: boolean;
  onExpand: (url: string) => void;
}) {
  const [failed, setFailed] = useState(false);
  const canExpand = Boolean(url) && !failed;
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={() => url && onExpand(url)}
        disabled={!canExpand}
        title={canExpand ? 'Click to expand' : undefined}
        className={`block h-20 w-20 rounded-lg border overflow-hidden bg-slate-900 ${
          canExpand ? 'cursor-zoom-in' : 'cursor-default'
        } ${highlight ? 'border-indigo-300' : 'border-slate-200'}`}
      >
        {url && !failed ? (
          <img
            src={url}
            alt={label}
            loading="lazy"
            onError={() => setFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-slate-500 text-[10px]">
            {url ? 'failed' : 'none'}
          </div>
        )}
      </button>
      <span className="text-[10px] font-mono text-slate-500 max-w-[80px] truncate" title={label}>
        {label}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const ok = status === 'success';
  const pending = isInProgress(status);
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold font-mono ${
        ok
          ? 'bg-success-100 text-success-700'
          : pending
            ? 'bg-slate-100 text-slate-500'
            : 'bg-danger-100 text-danger-700'
      }`}
      title={ok ? 'success' : pending ? `Still ${status}` : 'Provider error'}
    >
      {ok ? 'ok' : pending ? status : 'error'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Browse list
// ---------------------------------------------------------------------------

function RecentTraces({
  traces,
  error,
  onOpen,
}: {
  traces: ApiTraceRow[] | null;
  error: string | null;
  onOpen: (row: ApiTraceRow) => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">Recent scans</h2>
        <p className="text-xs text-slate-400 mt-0.5">
          Scans and jobs that sent calls to gen-api in the last 7 days. Pick one to see its
          sequence.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 text-sm text-danger">Failed to load recent scans: {error}</div>
      )}

      {!error && traces === null && (
        <div className="px-4 py-8 text-center text-sm text-slate-400">Loading…</div>
      )}

      {!error && traces !== null && traces.length === 0 && (
        <div className="px-4 py-12 text-center text-sm text-slate-400">
          No scan or job sent calls to gen-api in the last 7 days.
        </div>
      )}

      {!error && traces !== null && traces.length > 0 && (
        <div className="p-4">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th className="px-3 py-2">Scan</th>
                <th className="px-3 py-2">Job</th>
                <th className="px-3 py-2">Views</th>
                <th className="px-3 py-2 text-right">Calls</th>
                <th className="px-3 py-2 text-right">Errors</th>
                <th className="px-3 py-2 text-right">Cost</th>
                <th className="px-3 py-2">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {traces.map((row) => (
                <tr
                  key={`${row.scan_id}|${row.job_id}`}
                  onClick={() => onOpen(row)}
                  className="cursor-pointer"
                >
                  <td data-label="Scan" className="px-3 py-2 font-mono text-slate-700">
                    <span className="block max-w-[220px] truncate" title={row.scan_id}>
                      {row.scan_id || <span className="text-slate-300">—</span>}
                    </span>
                  </td>
                  <td data-label="Job" className="px-3 py-2 font-mono text-slate-500">
                    <span className="block max-w-[220px] truncate" title={row.job_id}>
                      {row.job_id || <span className="text-slate-300">—</span>}
                    </span>
                  </td>
                  <td data-label="Views" className="px-3 py-2 text-slate-500">
                    {row.views.length > 0 ? (
                      <span
                        className="block max-w-[220px] truncate"
                        title={row.views.join(', ')}
                      >
                        {row.views.length} · {row.views.join(', ')}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td data-label="Calls" className="px-3 py-2 text-right font-mono text-slate-700">
                    {row.calls}
                  </td>
                  <td data-label="Errors" className="px-3 py-2 text-right font-mono">
                    {row.errors > 0 ? (
                      <span className="text-danger font-semibold">{row.errors}</span>
                    ) : (
                      <span className="text-slate-300">0</span>
                    )}
                  </td>
                  <td data-label="Cost" className="px-3 py-2 text-right font-mono text-slate-700">
                    {formatUsd(row.cost_usd)}
                  </td>
                  <td data-label="Last activity" className="px-3 py-2 text-slate-500 whitespace-nowrap">
                    {formatTimestamp(row.last_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
